import { DataProvider } from "@shared/constants/common";
import { TicketBasket } from "@shared/modules/basket/types";

import { PAYMENT_MANAGER_CONFIRMATION_STRATEGY } from "@shared/modules/payments/utils/manager/utils";
import PaymentGateway from "../gateways/PaymentGateway";
import useBasketPaymentService, {
  PaymentServiceBasket,
} from "../services/useBasketPaymentService";
import useInvoicePaymentService from "../services/useInvoicePaymentService";
import { PurchaseOptsType } from "../types";
import type { PaymentManagerConfig } from "../PaymentManager";
import { PaymentConfigurationError } from "../utils/errors/PaymentConfigurationError";
import { PaymentSystemError } from "../utils/errors/PaymentSystemError";

/* eslint-disable @typescript-eslint/no-unused-vars, class-methods-use-this */
/**
 * PaymentHandlers represents the logic that tells us how to process a basket purchase
 * - based on the basket's characteristics
 * - based on an internal platform's requirements
 */
class PaymentHandler {
  public static readonly handlerName: string;

  protected basketPaymentService?: ReturnType<typeof useBasketPaymentService>;
  protected invoicePaymentService?: ReturnType<typeof useInvoicePaymentService>;

  /**
   * Whether atleast 1 of the handler's supported payment processor can have a potentially long (10min+) confirmation process
   * such is the case for things like 3DS, BNPLs where payment confirmation is done by the platform
   * ie. to process biometric authentication, identity capture for payment plans, etc.
   *
   * Not all handlers has asynchronous flows. Gateways may also support both asynchronous/synchronous flows simultaneously.
   */
  HAS_ASYNCHRONOUS_CONFIRMATION = false;

  /**
   * Whether atleast 1 of the handler's supported payment processors can potentially have a capture flow that asynchronously lets our systems know
   * that a payment has been captured.
   *
   * Not all handlers has asynchronous flows. Gateways may also support both asynchronous/synchronous flows simultaneously.
   */
  HAS_ASYNCHRONOUS_CAPTURE = false;

  /**
   * Whether this payment handler has been initialized.
   * The criteria that constitutes an 'initialized' state should be unique
   * to the payment handler.
   */
  is_initialized: boolean | null = null;

  /**
   * The gateway that this handler uses internally.
   * This should be narrowed down class that implements PaymentGateway.
   *
   * null - Some handlers may not require a gateway at all, which means `null` can be explicitly set.
   * undefined - (Default) - A handler has not set a gateway yet
   * PaymentGateway - A gateway that this handler supports
   */
  gateway: PaymentGateway | null | undefined = undefined;

  constructor(
    config: PaymentManagerConfig,
    basket: PaymentServiceBasket,
    servicesContext: {
      basketPaymentService?: ReturnType<typeof useBasketPaymentService>;
      invoicePaymentService?: ReturnType<typeof useInvoicePaymentService>;
    }
  ) {
    this.basketPaymentService = servicesContext.basketPaymentService;
    this.invoicePaymentService = servicesContext.invoicePaymentService;

    if (
      !Object.prototype.hasOwnProperty.call(this.constructor, "handlerName")
    ) {
      throw new PaymentConfigurationError(
        `Class ${this.constructor.name} must define a static 'handlerName' property.`,
        { metadata: { className: this.constructor.name } }
      );
    }
  }

  /**
   * Initializes a handler.
   * This initialization should be scoped to an organizer, not a checkout session.
   * This usually means the initialization of an internal service using an API key.
   *  @param basket The basket that we use to determine the handler's properties, such as which Gateway to use
   */
  initialize(basket: PaymentServiceBasket): Promise<this> {
    throw new PaymentSystemError(
      `Payment handler ${this.constructor.name} has not implemented the initialize method. This is a required method for all payment handlers.`,
      {
        metadata: {
          handlerName: this.constructor.name,
          method: "initialize",
          basketId: basket.basketId,
        },
      }
    );
  }

  /**
   * Processes a transaction
   */
  processCheckout(
    confirmationStrategy: PAYMENT_MANAGER_CONFIRMATION_STRATEGY,
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts?: PurchaseOptsType
  ): Promise<this | unknown> {
    throw new PaymentSystemError(
      `Payment handler ${this.constructor.name} has not implemented the processCheckout method. This is a required method for processing payments.`,
      {
        metadata: {
          handlerName: this.constructor.name,
          method: "processCheckout",
          confirmationStrategy,
          basketType,
          basketId: basket.basketId,
        },
      }
    );
  }

  postProcessCheckout(): Promise<TicketBasket> {
    throw new PaymentSystemError(
      `Payment handler ${this.constructor.name} has not implemented the postProcessCheckout method. This is a required method for finalizing payments.`,
      {
        metadata: {
          handlerName: this.constructor.name,
          method: "postProcessCheckout",
        },
      }
    );
  }

  /**
   * Updates the services used by this handler and its gateway
   */
  setServices(services: {
    basketPaymentService: ReturnType<typeof useBasketPaymentService>;
    invoicePaymentService: ReturnType<typeof useInvoicePaymentService>;
  }): void {
    // Update the gateway's services if it exists
    if (this.gateway) {
      this.gateway.setServices(services);
    }
  }
}

export default PaymentHandler;
